import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabase.js'

// useGroupReorderBrief — AI "this week" reorder brief lifecycle hook for the
// cross-unit consolidated reorder surface (Ship 6). The reorder analogue of
// useGroupMenuEngineeringRead.
//
//   1. On mount (and when groupId resolves), look up the row in
//      ai_group_briefs for (group_id, tab_key='reorder') where
//      generated_at > now() - 6h. Cache hit → phase='ready' (or 'empty'
//      when payload._empty). Miss → phase='generating', fire the edge fn.
//   2. regenerate({ bypassCache }) — explicit refresh past the window
//      (the Refresh control).
//   3. inFlightRef synchronous double-fire guard.
//   4. Calm deterministic fallback on error/empty — never the raw error.
//
// DECOUPLED from useGroupReorder (the list data): the list must render even
// while this brief is generating / empty / failed.

const TAB_KEY = 'reorder'
const CACHE_WINDOW_HOURS = 6

export function useGroupReorderBrief(groupId) {
  // --- All hooks declared BEFORE any conditional return (React #310). ---
  const [phase, setPhase]       = useState('idle')   // idle | generating | ready | empty | error
  const [brief, setBrief]       = useState(null)
  const [error, setError]       = useState(null)
  const [generatedAt, setGenAt] = useState(null)

  const inFlightRef = useRef(false)

  const lookupAndMaybeGenerate = useCallback(async ({ bypassCache = false } = {}) => {
    if (!groupId) return
    if (inFlightRef.current) return

    setError(null)

    // 1. Cache lookup (skipped on regenerate).
    if (!bypassCache) {
      const cutoff = new Date(Date.now() - CACHE_WINDOW_HOURS * 60 * 60 * 1000).toISOString()
      const { data: rows, error: cacheErr } = await supabase
        .from('ai_group_briefs')
        .select('generated_at, payload')
        .eq('group_id', groupId)
        .eq('tab_key', TAB_KEY)
        .gte('generated_at', cutoff)
        .order('generated_at', { ascending: false })
        .limit(1)

      if (!cacheErr && rows && rows.length > 0) {
        const row = rows[0]
        setBrief(row.payload || null)
        setGenAt(row.generated_at)
        setPhase(row.payload && row.payload._empty ? 'empty' : 'ready')
        return
      }
      if (cacheErr) {
        // eslint-disable-next-line no-console
        console.warn('[useGroupReorderBrief] cache read failed', cacheErr)
      }
    }

    // 2. Cache miss → call the edge fn. Claim the in-flight slot
    //    synchronously BEFORE the await.
    inFlightRef.current = true
    setPhase('generating')
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token || anonKey

      const resp = await fetch(`${supabaseUrl}/functions/v1/generate-group-reorder-brief`, {
        method: 'POST',
        headers: {
          'content-type':  'application/json',
          'authorization': `Bearer ${token}`,
          'apikey':        anonKey,
        },
        body: JSON.stringify({ group_id: groupId }),
      })
      if (!resp.ok) {
        const errText = await resp.text()
        throw new Error(`HTTP ${resp.status}: ${errText.slice(0, 200) || resp.statusText}`)
      }
      const data = await resp.json()
      if (data?.error) {
        setPhase('error')
        setError(data.message || "Couldn't pull your group reorder brief — tap to try again.")
        return
      }
      setBrief(data || null)
      setGenAt(new Date().toISOString())
      setPhase(data?._empty ? 'empty' : 'ready')
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useGroupReorderBrief] edge fn call failed', e)
      // Deterministic calm fallback. Never the raw error.
      setPhase('error')
      setError("Couldn't pull your group reorder brief — tap to try again.")
    } finally {
      inFlightRef.current = false
    }
  }, [groupId])

  // Initial load — kick off once groupId resolves.
  useEffect(() => {
    if (!groupId) return
    lookupAndMaybeGenerate({ bypassCache: false })
  }, [groupId, lookupAndMaybeGenerate])

  const regenerate = useCallback(({ bypassCache = true } = {}) => {
    if (inFlightRef.current) return
    return lookupAndMaybeGenerate({ bypassCache })
  }, [lookupAndMaybeGenerate])

  return useMemo(
    () => ({ phase, brief, error, generatedAt, regenerate }),
    [phase, brief, error, generatedAt, regenerate],
  )
}
